import { Navigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useStartupContext } from '../contexts/startup-guard'
import { useWorkspaceReady } from '../hooks/use-workspace-ready'

/**
 * Blocks direct URL access to routes that depend on a fully-set-up workspace
 * (deck processed + matcher produced a list). Mirrors the sidebar nav gate
 * so the URL bar can't bypass it.
 *
 * Behaviour:
 *   - Ready → render children.
 *   - Not ready + resolved → redirect to the dashboard.
 *   - Not ready + not resolved (cache says "no", live query still loading) →
 *     show a brief spinner instead of redirecting immediately. This avoids
 *     flashing the user back to the dashboard while the matches query lands.
 */
export function WorkspaceReadyGuard({ children }: { children: ReactNode }) {
  const { startup } = useStartupContext()
  const { ready, isResolved } = useWorkspaceReady()

  if (ready) return <>{children}</>

  if (!isResolved) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="size-10 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return <Navigate to="/app/startup/$id" params={{ id: startup.id }} replace />
}
